import * as intersection from 'lodash/intersection';

import { AirportSearchResult } from '../../airport/models/airport-search-result.model';
import { ApproachType } from '../../airport/models/approach-type.model';
import { ENCODED_VALUE, Filter, FilterKeys } from './filters';
import { FuelType } from '../../business/models/business-fuel.model';
import { QueryValidationReason, QueryValidationResult } from '../models/query-validation-result.model';
import { isArray, isNumeric } from '../../util/helpers';


/**
 * Minimum runway length filter. The filter passes only if the given airport search result has at least one runway
 * whose length is equal to or greater the value of this filter.
 */
export class RunwayLengthFilter extends Filter {
  constructor(public length: number = null) {
    super({
      key: FilterKeys.RunwayLength,
      apply: (airport: AirportSearchResult) => this._apply(airport),
      encode: () => this._encode(),
      decode: (v: ENCODED_VALUE) => this._decode(v),
      validate: () => this._validate()
    });
  }

  private _apply(airport: AirportSearchResult): boolean {
    return airport.maxRunwayLength >= this.length;
  }

  private _encode(): string {
    return this.enabled && this.length ? this.length.toString() : null;
  }

  private _decode(v: ENCODED_VALUE): boolean {
    if (isNumeric(v)) {
      this.length = parseInt(v.toString(), 10);
      return true;
    }
    return false;
  }

  private _validate(): QueryValidationResult {
    if (this.length <= 0) {
      return {
        valid: false,
        reason: QueryValidationReason.MIN_RUNWAY_LENGTH,
        message: 'Minimum runway length must be greater than zero.'
      };
    }

    return { valid: true };
  }
}


/**
 * Fuel type filter. The filter passes only if the given airport search result instance has the given fuel type
 * available.
 */
export class FuelFilter extends Filter {
  constructor(public fuelType: FuelType, public additionalTypes: Array<FuelType> = []) {
    super({
      key: FilterKeys.FuelFilter,
      multi: true,
      apply: (airport: AirportSearchResult) => this._apply(airport),
      encode: () => this._encode(),
      decode: (v: ENCODED_VALUE) => this._decode(v),
      validate: () => this._validate()
    });
  }

  private _apply(airport: AirportSearchResult): boolean {
    const availableTypes = airport.fuel.map(f => f.fuelType);
    const validTypes = [].concat(this.fuelType, this.additionalTypes);
    return intersection(availableTypes, validTypes).length > 0;
  }

  private _encode(): string {
    return this.enabled ? this.fuelType : null;
  }

  private _decode(v: ENCODED_VALUE): boolean {
    if (isArray(v)) {
      return v.some(f => this.fuelType === f);
    }
    return false;
  }

  private _validate(): QueryValidationResult {
    return { valid: true };
  }
}


/**
 * Approach type filter. The filter passes only if the airport search result instance has at least one instrument
 * approach of the specified type.
 */
export class ApproachFilter extends Filter {
  constructor(public approach: ApproachType) {
    super({
      key: FilterKeys.Approach,
      multi: true,
      apply: (airport: AirportSearchResult) => this._apply(airport),
      encode: () => this._encode(),
      decode: (v: ENCODED_VALUE) => this._decode(v),
      validate: () => this._validate()
    });
  }

  private _apply(airport: AirportSearchResult): boolean {
    return this.enabled && airport.approaches.includes(this.approach);
  }

  private _encode(): string {
    return this.enabled ? this.approach : null;
  }

  private _decode(v: ENCODED_VALUE): boolean {
    if (isArray(v)) {
      return v.some(f => this.approach === f);
    }
    return false;
  }

  private _validate(): QueryValidationResult {
    return { valid: true };
  }
}


/**
 * Airport service type
 */
export enum ServiceType {
  AirframeRepair = 'airframe',
  EngineRepair = 'engine',
  AvionicsRepair = 'avionics'
}


/**
 * Service filter. The filter passes only if the airport search result instance offers the corresponding service
 * (e.g., airframe or engine repair).
 */
export class ServiceFilter extends Filter {
  constructor(public service: ServiceType) {
    super({
      key: FilterKeys.Services,
      multi: true,
      apply: (airport: AirportSearchResult) => this._apply(airport),
      encode: () => this._encode(),
      decode: (v: ENCODED_VALUE) => this._decode(v),
      validate: () => this._validate()
    });
  }

  private _apply(airport: AirportSearchResult): boolean {
    if (!this.enabled) {
      return false;
    }
    switch (this.service) {
      case ServiceType.AirframeRepair:
        return airport.airframeRepair;
      case ServiceType.EngineRepair:
        return airport.engineRepair;
      case ServiceType.AvionicsRepair:
        return airport.avionicsRepair;
    }
    return false;
  }

  private _encode(): string {
    return this.enabled ? this.service : null;
  }

  private _decode(v: ENCODED_VALUE): boolean {
    if (isArray(v)) {
      return v.some(f => this.service === f);
    }
    return false;
  }

  private _validate(): QueryValidationResult {
    return { valid: true };
  }
}
